// Field/column configuration for all config-driven CRUD modules.

export const UNITS = ["MT", "KG", "GM", "Litre", "ML", "Bag", "PCS", "Piece", "Box", "Roll", "Other"];

const customerSelect = {
  name: "customer_id", label: "Customer", type: "select", optionsEndpoint: "customers",
  optionLabel: "company_name", linkName: "customer_name", activeOnly: true, required: true,
  copyFields: { billing_address: "billing_address", shipping_address: "shipping_address", payment_terms: "payment_terms" },
};
const supplierSelect = {
  name: "supplier_id", label: "Supplier", type: "select", optionsEndpoint: "suppliers",
  optionLabel: "supplier_name", linkName: "supplier_name", required: true,
};
const productSelectSales = {
  name: "product_id", label: "Product / Item", type: "select", optionsEndpoint: "products",
  optionLabel: "product_name", linkName: "product_name", activeOnly: true, required: true,
  copyFields: { unit: "unit", rate: "sales_rate" },
};
const productSelectPurchase = {
  name: "product_id", label: "Product / Item", type: "select", optionsEndpoint: "products",
  optionLabel: "product_name", linkName: "product_name", activeOnly: true, required: true,
  copyFields: { unit: "unit", rate: "purchase_rate" },
};

const statusField = (opts) => ({ name: "status", label: "Status", type: "select", options: opts });

const saleColumns = (refKey, refLabel, partyKey) => [
  { key: refKey, label: refLabel },
  { key: partyKey, label: partyKey === "customer_name" ? "Customer" : "Supplier" },
  { key: "product_name", label: "Product" },
  { key: "quantity", label: "Qty", format: "qty" },
  { key: "unit", label: "Unit" },
  { key: "total", label: "Total", format: "currency" },
  { key: "status", label: "Status", format: "badge" },
];

const saleFields = (refKey, refLabel, dateKey, dateLabel, partySelect, productSelect, statusOpts, extra = []) => [
  { name: refKey, label: refLabel, type: "text", required: true },
  partySelect,
  { name: dateKey, label: dateLabel, type: "date" },
  ...extra,
  productSelect,
  { name: "quantity", label: "Quantity", type: "number", required: true, step: "0.001", min: "0" },
  { name: "unit", label: "Unit", type: "select", options: UNITS },
  { name: "rate", label: "Rate (₹)", type: "number", required: true, min: "0" },
  { name: "discount", label: "Discount (₹)", type: "number", min: "0" },
  { name: "tax_percent", label: "Tax %", type: "number", min: "0" },
  { name: "notes", label: "Notes", type: "textarea" },
  statusField(statusOpts),
];

export const MODULES = {
  customers: {
    title: "Customers", endpoint: "customers", detailPath: "/customers",
    ledgerType: "customer",
    searchKeys: ["company_name", "city", "contact_person"],
    statusFilter: ["Active", "Inactive"],
    columns: [
      { key: "company_name", label: "Company" },
      { key: "contact_person", label: "Contact" },
      { key: "city", label: "City" },
      { key: "mobile", label: "Mobile" },
      { key: "credit_limit", label: "Credit Limit", format: "currency" },
      { key: "status", label: "Status", format: "badge" },
    ],
    fields: [
      { name: "company_name", label: "Company Name", type: "text", required: true },
      { name: "contact_person", label: "Contact Person", type: "text" },
      { name: "mobile", label: "Mobile", type: "text", validate: "mobile" },
      { name: "whatsapp", label: "WhatsApp", type: "text" },
      { name: "email", label: "Email", type: "text", validate: "email" },
      { name: "gst_registration_status", label: "GST Registration", type: "select", options: ["Registered", "Unregistered / Consumer"] },
      { name: "gst_number", label: "GST Number", type: "text", validate: "gst" },
      { name: "pan_number", label: "PAN Number", type: "text", validate: "pan" },
      { name: "billing_address", label: "Billing Address", type: "textarea" },
      { name: "shipping_address", label: "Shipping Address", type: "textarea" },
      { name: "city", label: "City", type: "text" },
      { name: "state", label: "State", type: "text" },
      { name: "state_code", label: "State Code", type: "text" },
      { name: "pincode", label: "Pincode", type: "text" },
      { name: "payment_terms", label: "Payment Terms", type: "text" },
      { name: "credit_limit", label: "Credit Limit (₹)", type: "number", min: "0" },
      { name: "sales_person", label: "Sales Person", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
      statusField(["Active", "Inactive"]),
    ],
    defaults: { status: "Active" },
  },
  suppliers: {
    statementAction: "suppliers",
    ledgerType: "supplier",
    title: "Suppliers", endpoint: "suppliers",
    searchKeys: ["supplier_name", "contact_person"],
    columns: [
      { key: "supplier_name", label: "Supplier" },
      { key: "contact_person", label: "Contact" },
      { key: "mobile", label: "Mobile" },
      { key: "gst_number", label: "GST" },
      { key: "payment_terms", label: "Terms" },
    ],
    fields: [
      { name: "supplier_name", label: "Supplier Name", type: "text", required: true },
      { name: "supplier_code", label: "Supplier Code", type: "text" },
      { name: "contact_person", label: "Contact Person", type: "text" },
      { name: "mobile", label: "Mobile", type: "text", validate: "mobile" },
      { name: "email", label: "Email", type: "text", validate: "email" },
      { name: "gst_number", label: "GST Number", type: "text", validate: "gst" },
      { name: "pan_number", label: "PAN Number", type: "text", validate: "pan" },
      { name: "address", label: "Address", type: "textarea" },
      { name: "city", label: "City", type: "text" },
      { name: "state", label: "State", type: "text" },
      { name: "state_code", label: "State Code", type: "text" },
      { name: "pincode", label: "PIN Code", type: "text" },
      { name: "payment_terms", label: "Payment Terms", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  job_work_vendors: {
    title: "Job Work Vendors", endpoint: "job-work-vendors",
    searchKeys: ["vendor_name", "vendor_type", "contact_person", "mobile", "city"],
    statusFilter: ["Active", "Inactive"],
    columns: [
      { key: "vendor_code", label: "Code" },
      { key: "vendor_name", label: "Vendor" },
      { key: "vendor_type", label: "Type" },
      { key: "contact_person", label: "Contact" },
      { key: "mobile", label: "Mobile" },
      { key: "whatsapp", label: "WhatsApp" },
      { key: "city", label: "City" },
      { key: "payment_terms", label: "Terms" },
    ],
    fields: [
      { name: "vendor_name", label: "Vendor / Company Name", type: "text", required: true },
      {
        name: "vendor_type", label: "Vendor Type", type: "select", required: true,
        options: ["Job Worker / Contract Manufacturer", "Transporter", "Labour Contractor",
          "Packaging Supplier", "Loading / Unloading Contractor", "Machine / Equipment Hire", "Other"],
      },
      { name: "contact_person", label: "Contact Person", type: "text" },
      { name: "mobile", label: "Mobile", type: "text", validate: "mobile" },
      { name: "whatsapp", label: "WhatsApp Number", type: "text", validate: "mobile" },
      { name: "email", label: "Email", type: "text", validate: "email" },
      { name: "gst_number", label: "GST Number", type: "text", validate: "gst" },
      { name: "pan_number", label: "PAN Number", type: "text", validate: "pan" },
      { name: "address", label: "Address", type: "textarea" },
      { name: "city", label: "City", type: "text" },
      { name: "state", label: "State", type: "text" },
      { name: "pincode", label: "PIN Code", type: "text" },
      { name: "rate_terms", label: "Rate / Job Charges", type: "text" },
      { name: "payment_terms", label: "Payment Terms", type: "text" },
      { name: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  products: {
    title: "Products", endpoint: "products",
    searchKeys: ["product_name", "grade", "category"],
    statusFilter: ["Active", "Inactive"],
    columns: [
      { key: "product_name", label: "Product" },
      { key: "grade", label: "Grade" },
      { key: "hsn_code", label: "HSN" },
      { key: "category", label: "Category" },
      { key: "unit", label: "Unit" },
      { key: "sales_rate", label: "Sales Rate", format: "currency" },
      { key: "current_stock", label: "Stock", format: "qty" },
      { key: "status", label: "Status", format: "badge" },
    ],
    fields: [
      { name: "product_name", label: "Product Name", type: "text", required: true },
      { name: "product_code", label: "Product Code / SKU", type: "text" },
      { name: "grade", label: "Grade", type: "text" },
      { name: "hsn_code", label: "HSN Code", type: "text", required: true, validate: "hsn_code" },
      { name: "tax_rate", label: "GST / Tax Rate (%)", type: "number", min: "0" },
      { name: "category", label: "Category", type: "select", options: ["Raw Material", "Finished Goods", "Packaging Material", "Consumable", "Other"] },
      { name: "unit", label: "Unit", type: "select", options: UNITS, required: true },
      { name: "purchase_rate", label: "Default / Reference Purchase Rate (₹)", type: "number", min: "0", hint: "Suggested on new purchase bills only — actual bill rate controls stock cost" },
      { name: "sales_rate", label: "Sales Rate (₹)", type: "number", min: "0" },
      { name: "min_stock", label: "Minimum Stock Level", type: "number", min: "0" },
      { name: "description", label: "Description", type: "textarea" },
      statusField(["Active", "Inactive"]),
    ],
    defaults: { status: "Active", unit: "MT" },
  },
  inventory: {
    title: "Inventory Movements", endpoint: "inventory", documentsType: "inventory",
    searchKeys: ["product_name", "warehouse", "batch_number", "reference_number"],
    statusFilter: null,
    columns: [
      { key: "movement_date", label: "Date", format: "date" },
      { key: "product_name", label: "Product" },
      { key: "movement_type", label: "Type", format: "badge" },
      { key: "quantity", label: "Qty", format: "qty" },
      { key: "unit", label: "Unit" },
      { key: "warehouse", label: "Warehouse" },
      { key: "batch_number", label: "Batch" },
      { key: "reference_number", label: "Ref" },
    ],
    fields: [
      productSelectPurchase,
      { name: "movement_type", label: "Movement Type", type: "select", options: ["opening", "purchase", "production", "sales", "dispatch", "sample", "adjustment-in", "adjustment-out"], required: true },
      { name: "quantity", label: "Quantity", type: "number", required: true, step: "0.001", min: "0" },
      { name: "unit", label: "Unit", type: "select", options: UNITS },
      { name: "warehouse", label: "Warehouse / Location", type: "text" },
      { name: "batch_number", label: "Batch Number", type: "text" },
      { name: "movement_date", label: "Date", type: "date" },
      { name: "reference_number", label: "Reference / Document No", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    defaults: { unit: "MT" },
    note: "Manual movements only. Purchase Bills & Invoices auto-create movements.",
  },
  production: {
    title: "Production", endpoint: "production",
    searchKeys: ["product_name", "batch_number", "production_location"],
    columns: [
      { key: "production_date", label: "Date", format: "date" },
      { key: "product_name", label: "Product" },
      { key: "batch_number", label: "Batch" },
      { key: "input_quantity", label: "Input", format: "qty" },
      { key: "output_quantity", label: "Output", format: "qty" },
      { key: "wastage", label: "Wastage", format: "qty" },
      { key: "production_location", label: "Location" },
    ],
    fields: [
      { name: "production_date", label: "Production Date", type: "date" },
      productSelectPurchase,
      { name: "batch_number", label: "Batch Number", type: "text" },
      { name: "raw_material", label: "Raw Material", type: "text" },
      { name: "input_quantity", label: "Input Quantity", type: "number", step: "0.001" },
      { name: "output_quantity", label: "Output Quantity", type: "number", step: "0.001" },
      { name: "wastage", label: "Wastage", type: "number", step: "0.001" },
      { name: "production_location", label: "Production Location", type: "text" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
  },
  dispatches: {
    title: "Dispatch", endpoint: "dispatches", pdfType: "dispatch",
    searchKeys: ["customer_name", "vehicle_number", "transporter", "invoice_no"],
    statusFilter: ["Pending", "In Transit", "Delivered", "Cancelled"], statusKey: "delivery_status",
    columns: [
      { key: "dispatch_date", label: "Date", format: "date" },
      { key: "customer_name", label: "Customer" },
      { key: "product_name", label: "Product" },
      { key: "quantity", label: "Qty", format: "qty" },
      { key: "vehicle_number", label: "Vehicle" },
      { key: "transporter", label: "Transporter" },
      { key: "delivery_status", label: "Status", format: "badge" },
    ],
    fields: [
      { ...customerSelect, copyFields: { contact_number: "mobile", destination_address: "shipping_address" } },
      { name: "contact_number", label: "Contact Number", type: "text" },
      { name: "destination_address", label: "Delivery Address", type: "textarea" },
      { name: "invoice_no", label: "Invoice No", type: "text" },
      { name: "product_name", label: "Product", type: "text" },
      { name: "quantity", label: "Quantity", type: "number", step: "0.001" },
      { name: "vehicle_number", label: "Vehicle Number", type: "text" },
      { name: "transporter", label: "Transporter", type: "text" },
      { name: "lr_number", label: "LR / GR Number", type: "text" },
      { name: "dispatch_date", label: "Dispatch Date", type: "date" },
      { name: "delivery_status", label: "Delivery Status", type: "select", options: ["Pending", "In Transit", "Delivered", "Cancelled"] },
    ],
  },
  employees: {
    title: "Employees", endpoint: "employees",
    advanceAction: "employees",
    searchKeys: ["employee_code", "name", "department", "designation", "phone", "email"],
    statusFilter: ["Active", "Inactive"],
    compute: (f) => ({
      monthly_salary: (Number(f.basic) || 0) + (Number(f.hra) || 0) + (Number(f.lta) || 0) + (Number(f.other_allowances) || 0),
    }),
    columns: [
      { key: "employee_code", label: "Code" }, { key: "name", label: "Name" }, { key: "department", label: "Department" },
      { key: "designation", label: "Designation" }, { key: "phone", label: "Phone" }, { key: "monthly_salary", label: "Gross Salary", format: "currency" },
      { key: "status", label: "Status", format: "badge" },
    ],
    fields: [
      { name: "name", label: "Full Name", type: "text", required: true },
      { name: "department", label: "Department", type: "select", options: ["Production", "QC / Lab", "Store", "Sales", "Purchase", "Accounts", "Dispatch", "Admin / HR", "Management"] },
      { name: "designation", label: "Designation", type: "text" },
      { name: "grade", label: "Grade", type: "text" },
      { name: "location", label: "Location", type: "text" },
      { name: "phone", label: "Phone", type: "text", validate: "mobile" },
      { name: "email", label: "Email", type: "text", validate: "email" },
      { name: "dob", label: "Date of Birth", type: "date" },
      { name: "join_date", label: "Date of Joining", type: "date" },
      { name: "basic", label: "Basic (₹)", type: "number", min: "0", required: true },
      { name: "hra", label: "HRA (₹)", type: "number", min: "0" },
      { name: "lta", label: "Leave Travel Allowance (₹)", type: "number", min: "0" },
      { name: "other_allowances", label: "Other Allowance (₹)", type: "number", min: "0" },
      { name: "monthly_salary", label: "Gross Salary (₹) — auto", type: "number", min: "0", readOnly: true },
      { name: "pf", label: "PF Deduction (₹)", type: "number", min: "0" },
      { name: "esic", label: "ESIC (₹)", type: "number", min: "0" },
      { name: "pt", label: "Professional Tax (₹)", type: "number", min: "0" },
      { name: "income_tax", label: "Income Tax / TDS (₹)", type: "number", min: "0" },
      { name: "insurance_premium", label: "Insurance Premium (₹)", type: "number", min: "0" },
      { name: "leave_quota", label: "Annual Leave Quota (days)", type: "number", min: "0" },
      { name: "leave_taken", label: "Leave Already Taken (days)", type: "number", min: "0", step: "0.5" },
      { name: "uan_no", label: "UAN No.", type: "text" },
      { name: "pan", label: "PAN No.", type: "text", validate: "pan" },
      { name: "esic_no", label: "ESIC No.", type: "text" },
      { name: "bank_name", label: "Bank Name", type: "text" },
      { name: "account_number", label: "Bank A/C Number", type: "text" },
      { name: "ifsc", label: "IFSC Code", type: "text" },
      { name: "address", label: "Address", type: "textarea" },
      statusField(["Active", "Inactive"]),
    ],
    defaults: { status: "Active", leave_quota: 24 },
  },
  attendance: {
    title: "Attendance", endpoint: "attendance",
    searchKeys: ["employee_name", "status", "attendance_date"],
    statusFilter: ["Present", "Absent", "Half Day", "Leave", "LWP", "Holiday"],
    columns: [{ key: "attendance_date", label: "Date", format: "date" }, { key: "employee_name", label: "Employee" }, { key: "status", label: "Status", format: "badge" }, { key: "in_time", label: "In" }, { key: "out_time", label: "Out" }, { key: "remarks", label: "Remarks" }],
    fields: [
      { name: "attendance_date", label: "Date", type: "date", required: true },
      { name: "employee_id", label: "Employee", type: "select", optionsEndpoint: "employees", optionLabel: "name", linkName: "employee_name", activeOnly: true, required: true },
      { name: "status", label: "Status", type: "select", required: true, options: ["Present", "Absent", "Half Day", "Leave", "LWP", "Holiday"] },
      { name: "in_time", label: "In Time", type: "text" },
      { name: "out_time", label: "Out Time", type: "text" },
      { name: "remarks", label: "Remarks", type: "text" },
    ],
    defaults: { status: "Present" },
  },
  leaves: {
    title: "Leave Requests", endpoint: "leaves",
    leaveActions: true,
    searchKeys: ["employee_name", "leave_type", "status"],
    statusFilter: ["Pending", "Approved", "Rejected"],
    columns: [{ key: "employee_name", label: "Employee" }, { key: "leave_type", label: "Type" }, { key: "from_date", label: "From", format: "date" }, { key: "to_date", label: "To", format: "date" }, { key: "days", label: "Days" }, { key: "status", label: "Status", format: "badge" }],
    fields: [
      { name: "employee_id", label: "Employee", type: "select", optionsEndpoint: "employees", optionLabel: "name", linkName: "employee_name", activeOnly: true, required: true },
      { name: "leave_type", label: "Leave Type", type: "select", required: true, options: ["Casual", "Sick", "Paid", "Unpaid", "Other"] },
      { name: "from_date", label: "From", type: "date", required: true },
      { name: "to_date", label: "To", type: "date", required: true },
      { name: "days", label: "Days", type: "number", min: "0", required: true },
      { name: "reason", label: "Reason", type: "textarea" },
    ],
    defaults: { status: "Pending" },
  },
  payroll: {
    title: "Payroll / Salary Slips", endpoint: "payroll",
    slipAction: true,
    markPaid: true,
    generate: { endpoint: "payroll/generate", optionsKey: "employee_id", idsKey: "employee_ids", label: "Generate Payroll" },
    compute: (f) => {
      const gross = ["basic", "hra", "lta", "other_allowances", "arrears"].reduce((s, k) => s + (Number(f[k]) || 0), 0);
      const ded = ["pf", "esic", "pt", "income_tax", "insurance_premium", "loan", "salary_advance", "other_deduction", "lop_amount"].reduce((s, k) => s + (Number(f[k]) || 0), 0);
      return { gross_pay: gross, total_deductions: ded, net_pay: gross - ded };
    },
    searchKeys: ["slip_no", "employee_name", "month"],
    statusFilter: ["Paid", "Unpaid"],
    columns: [{ key: "slip_no", label: "Slip No" }, { key: "employee_name", label: "Employee" }, { key: "month", label: "Month" }, { key: "gross_pay", label: "Gross", format: "currency" }, { key: "total_deductions", label: "Deductions", format: "currency" }, { key: "net_pay", label: "Net Pay", format: "currency" }, { key: "status", label: "Status", format: "badge" }],
    fields: [
      { name: "employee_id", label: "Employee", type: "select", optionsEndpoint: "employees", optionLabel: "name", linkName: "employee_name", activeOnly: true, required: true },
      { name: "month", label: "Month (e.g. Jun 2026)", type: "text", required: true },
      { name: "basic", label: "Basic (₹)", type: "number", min: "0", required: true },
      { name: "hra", label: "HRA (₹)", type: "number", min: "0" },
      { name: "lta", label: "Leave Travel Allowance (₹)", type: "number", min: "0" },
      { name: "other_allowances", label: "Other Allowance (₹)", type: "number", min: "0" },
      { name: "arrears", label: "Arrears (₹)", type: "number", min: "0" },
      { name: "pf", label: "PF (₹)", type: "number", min: "0" },
      { name: "esic", label: "ESIC (₹)", type: "number", min: "0" },
      { name: "pt", label: "Professional Tax (₹)", type: "number", min: "0" },
      { name: "income_tax", label: "Income Tax (₹)", type: "number", min: "0" },
      { name: "insurance_premium", label: "Insurance Premium (₹)", type: "number", min: "0" },
      { name: "loan", label: "Loan (₹)", type: "number", min: "0" },
      { name: "salary_advance", label: "Salary Advance (₹)", type: "number", min: "0" },
      { name: "other_deduction", label: "Other Deduction (₹)", type: "number", min: "0" },
      { name: "gross_pay", label: "Gross Amount (₹) — auto", type: "number", readOnly: true },
      { name: "total_deductions", label: "Deduction Amount (₹) — auto", type: "number", readOnly: true },
      { name: "net_pay", label: "Net Payable (₹) — auto", type: "number", readOnly: true },
      { name: "payment_mode", label: "Payment Mode", type: "select", options: ["Bank Transfer", "Cash", "Cheque", "UPI"] },
      statusField(["Paid", "Unpaid"]),
    ],
    defaults: { status: "Unpaid" },
  },
  daily_workers: {
    title: "Daily Workers", endpoint: "daily-workers",
    advanceAction: "daily-workers",
    searchKeys: ["worker_code", "name", "job_role", "department", "phone"],
    statusFilter: ["Active", "Inactive"],
    columns: [
      { key: "worker_code", label: "Code" }, { key: "name", label: "Name" }, { key: "job_role", label: "Job Role" },
      { key: "department", label: "Department" }, { key: "daily_rate", label: "Daily Rate", format: "currency" },
      { key: "overtime_rate", label: "OT Rate/hr", format: "currency" }, { key: "status", label: "Status", format: "badge" },
    ],
    fields: [
      { name: "name", label: "Worker Name", type: "text", required: true },
      { name: "job_role", label: "Job Role", type: "text" },
      { name: "department", label: "Department", type: "select", options: ["Production", "QC / Lab", "Store", "Dispatch", "Loading / Unloading", "Packing", "Maintenance", "General", "Other"] },
      { name: "phone", label: "Phone", type: "text", validate: "mobile" },
      { name: "daily_rate", label: "Daily Wage Rate (₹)", type: "number", min: "0", required: true },
      { name: "overtime_rate", label: "Overtime Rate (₹/hr)", type: "number", min: "0" },
      statusField(["Active", "Inactive"]),
    ],
    defaults: { status: "Active" },
  },
  daily_attendance: {
    title: "Daily Attendance", endpoint: "daily-attendance",
    bulkPath: "/daily-attendance/bulk",
    searchKeys: ["worker_name", "status", "work_date"],
    statusFilter: ["Present", "Absent", "Half Day"],
    columns: [{ key: "work_date", label: "Date", format: "date" }, { key: "worker_name", label: "Worker" }, { key: "status", label: "Status", format: "badge" }, { key: "overtime_hours", label: "OT Hrs" }, { key: "remarks", label: "Remarks" }],
    fields: [
      { name: "work_date", label: "Date", type: "date", required: true },
      { name: "worker_id", label: "Worker", type: "select", optionsEndpoint: "daily-workers", optionLabel: "name", linkName: "worker_name", activeOnly: true, required: true },
      { name: "status", label: "Status", type: "select", required: true, options: ["Present", "Absent", "Half Day"] },
      { name: "overtime_hours", label: "Overtime Hours", type: "number", min: "0" },
      { name: "remarks", label: "Remarks", type: "text" },
    ],
    defaults: { status: "Present" },
  },
  daily_wages: {
    title: "Daily Wage Statements", endpoint: "daily-wages",
    slipAction: "daily-wage",
    markPaid: true,
    register: { pdf: "daily-wage-register", xlsx: "daily-wages/register/xlsx", label: "Wage Register" },
    generate: { endpoint: "daily-wages/generate", optionsKey: "worker_id", idsKey: "worker_ids", label: "Generate Wages" },
    compute: (f) => {
      const gross = (Number(f.present_days) || 0) * (Number(f.daily_rate) || 0) + (Number(f.half_days) || 0) * 0.5 * (Number(f.daily_rate) || 0) + (Number(f.overtime_hours) || 0) * (Number(f.overtime_rate) || 0);
      const ded = (Number(f.advances) || 0) + (Number(f.other_deduction) || 0);
      return { gross_wages: gross, total_deductions: ded, net_payable: gross - ded };
    },
    searchKeys: ["statement_no", "worker_name", "month"],
    statusFilter: ["Paid", "Unpaid"],
    columns: [{ key: "statement_no", label: "Statement" }, { key: "worker_name", label: "Worker" }, { key: "month", label: "Month" }, { key: "present_days", label: "Present" }, { key: "overtime_hours", label: "OT Hrs" }, { key: "gross_wages", label: "Gross", format: "currency" }, { key: "net_payable", label: "Net Payable", format: "currency" }, { key: "status", label: "Status", format: "badge" }],
    fields: [
      { name: "worker_id", label: "Worker", type: "select", optionsEndpoint: "daily-workers", optionLabel: "name", linkName: "worker_name", activeOnly: true, required: true, copyFields: { daily_rate: "daily_rate", overtime_rate: "overtime_rate" } },
      { name: "month", label: "Month (e.g. Jun 2026)", type: "text", required: true },
      { name: "present_days", label: "Present Days", type: "number", min: "0" },
      { name: "half_days", label: "Half Days", type: "number", min: "0" },
      { name: "absent_days", label: "Absent Days", type: "number", min: "0" },
      { name: "daily_rate", label: "Daily Rate (₹)", type: "number", min: "0" },
      { name: "overtime_hours", label: "Overtime Hours", type: "number", min: "0" },
      { name: "overtime_rate", label: "Overtime Rate (₹/hr)", type: "number", min: "0" },
      { name: "advances", label: "Advances (₹)", type: "number", min: "0" },
      { name: "other_deduction", label: "Other Deduction (₹)", type: "number", min: "0" },
      { name: "gross_wages", label: "Gross Wages (₹) — auto", type: "number", readOnly: true },
      { name: "total_deductions", label: "Deductions (₹) — auto", type: "number", readOnly: true },
      { name: "net_payable", label: "Net Payable (₹) — auto", type: "number", readOnly: true },
      { name: "payment_mode", label: "Payment Mode", type: "select", options: ["Cash", "Bank Transfer", "UPI", "Cheque"] },
      statusField(["Paid", "Unpaid"]),
    ],
    defaults: { status: "Unpaid", payment_mode: "Cash" },
  },
  expenses: {
    title: "Expenses", endpoint: "expenses", payAction: true,
    slipAction: "expense",
    register: { pdf: "expense-register", xlsx: "expenses/register/xlsx", label: "Expense Register" },
    searchKeys: ["voucher_no", "category", "description", "paid_to", "payment_mode", "reference_number"],
    statusFilter: ["Paid", "Unpaid"],
    columns: [
      { key: "voucher_no", label: "Voucher" },
      { key: "expense_date", label: "Date", format: "date" },
      { key: "category", label: "Category" },
      { key: "description", label: "Description" },
      { key: "amount", label: "Amount", format: "currency" },
      { key: "payment_mode", label: "Mode" },
      { key: "status", label: "Status", format: "badge" },
    ],
    fields: [
      { name: "expense_date", label: "Date", type: "date", required: true },
      { name: "category", label: "Category", type: "select", required: true, options: ["Rent", "Electricity", "Water", "Transport / Freight", "Salaries / Wages", "Office Supplies", "Repairs & Maintenance", "Telephone / Internet", "Fuel", "Bank Charges", "Professional Fees", "Marketing", "Taxes & Duties", "Miscellaneous"] },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "amount", label: "Amount (₹)", type: "number", required: true, min: "0" },
      { name: "payment_mode", label: "Payment Mode", type: "select", options: ["Cash", "Bank Transfer", "Cheque", "UPI", "NEFT/RTGS"] },
      { name: "paid_to", label: "Paid To / Vendor", type: "text" },
      { name: "vehicle_no", label: "Vehicle No", type: "text", showIf: (f) => f.category === "Transport / Freight" },
      { name: "invoice_id", label: "Against Sales Invoice (for freight check)", type: "select", optionsEndpoint: "invoices", optionLabel: "invoice_no", linkName: "invoice_no", showIf: (f) => f.category === "Transport / Freight" },
      { name: "transporter_name", label: "Transporter / Driver Name", type: "text", showIf: (f) => f.category === "Transport / Freight" },
      { name: "transporter_mobile", label: "Mobile Number", type: "text", showIf: (f) => f.category === "Transport / Freight" },
      { name: "reference_number", label: "Reference / Bill No", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
      statusField(["Paid", "Unpaid"]),
    ],
    defaults: { status: "Paid", payment_mode: "Cash" },
  },
  payments: {
    title: "Payments", endpoint: "payments",
    receiptAction: true,
    searchKeys: ["party_name", "reference_number", "payment_mode"],
    columns: [
      { key: "payment_date", label: "Date", format: "date" },
      { key: "party_name", label: "Party" },
      { key: "party_type", label: "Type", format: "badge" },
      { key: "amount", label: "Amount", format: "currency" },
      { key: "payment_mode", label: "Mode" },
      { key: "reference_number", label: "Reference" },
    ],
    fields: [
      { name: "party", label: "Party (Customer / Supplier)", type: "party", required: true },
      { name: "amount", label: "Amount (₹)", type: "number", required: true, min: "0" },
      { name: "payment_date", label: "Payment Date", type: "date" },
      { name: "payment_mode", label: "Payment Mode", type: "select", options: ["Cash", "Bank Transfer", "Cheque", "UPI", "NEFT/RTGS"] },
      { name: "reference_number", label: "Reference Number", type: "text" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
  },
  quotations: {
    title: "Quotations", endpoint: "quotations", pdfType: "quotation", convertible: true,
    searchKeys: ["quotation_no", "customer_name", "product_name"],
    statusFilter: ["Draft", "Sent", "Accepted", "Rejected", "Expired", "Cancelled"],
    columns: saleColumns("quotation_no", "Quote No", "customer_name"),
    fields: saleFields("quotation_no", "Quotation No", "quotation_date", "Quotation Date", customerSelect, productSelectSales,
      ["Draft", "Sent", "Accepted", "Rejected", "Expired", "Cancelled"],
      [{ name: "valid_until", label: "Valid Until", type: "date" }]),
    defaults: { status: "Draft", unit: "MT", discount: 0, tax_percent: 18 },
  },
  "sales-orders": {
    title: "Sales Orders", endpoint: "sales-orders",
    searchKeys: ["order_no", "customer_name", "product_name"],
    statusFilter: ["Pending", "Confirmed", "Completed", "Cancelled"],
    columns: saleColumns("order_no", "Order No", "customer_name"),
    fields: saleFields("order_no", "Order No", "order_date", "Order Date", customerSelect, productSelectSales,
      ["Pending", "Confirmed", "Completed", "Cancelled"]),
    defaults: { status: "Pending", unit: "MT", tax_percent: 18 },
  },
  invoices: {
    title: "Invoices", endpoint: "invoices", pdfType: "invoice",
    searchKeys: ["invoice_no", "customer_name", "product_name"],
    statusFilter: ["Unpaid", "Partial", "Paid", "Cancelled"],
    columns: [
      { key: "invoice_no", label: "Invoice No" },
      { key: "customer_name", label: "Customer" },
      { key: "product_name", label: "Product" },
      { key: "quantity", label: "Qty", format: "qty" },
      { key: "total", label: "Total", format: "currency" },
      { key: "balance_amount", label: "Balance", format: "currency" },
      { key: "status", label: "Status", format: "badge" },
    ],
    fields: saleFields("invoice_no", "Invoice No", "invoice_date", "Invoice Date", customerSelect, productSelectSales,
      ["Unpaid", "Partial", "Paid", "Cancelled"],
      [{ name: "due_date", label: "Due Date", type: "date" }, { name: "payment_terms", label: "Payment Terms", type: "text" }]).concat([
        { name: "paid_amount", label: "Paid Amount (₹)", type: "number", min: "0" },
        { name: "paid_mode", label: "Paid Via (Cash / Bank)", type: "select", options: ["Cash", "Bank Transfer", "Cheque", "UPI", "NEFT/RTGS"] },
        { name: "billing_address", label: "Billing Address", type: "textarea" },
        { name: "shipping_address", label: "Shipping Address", type: "textarea" },
      ]),
    defaults: { status: "Unpaid", unit: "MT", discount: 0, tax_percent: 18, paid_amount: 0, paid_mode: "Bank Transfer" },
  },
  "purchase-orders": {
    title: "Purchase Orders", endpoint: "purchase-orders",
    searchKeys: ["po_no", "supplier_name", "product_name"],
    statusFilter: ["Pending", "Confirmed", "Received", "Cancelled"],
    columns: saleColumns("po_no", "PO No", "supplier_name"),
    fields: saleFields("po_no", "PO No", "po_date", "PO Date", supplierSelect, productSelectPurchase,
      ["Pending", "Confirmed", "Received", "Cancelled"]),
    defaults: { status: "Pending", unit: "MT", tax_percent: 18 },
  },
  "purchase-bills": {
    title: "Purchase Bills", endpoint: "purchase-bills", pdfType: "purchase_bill",
    searchKeys: ["bill_no", "supplier_name", "product_name"],
    statusFilter: ["Unpaid", "Partial", "Paid", "Cancelled"],
    columns: saleColumns("bill_no", "Bill No", "supplier_name"),
    fields: saleFields("bill_no", "Bill No", "bill_date", "Bill Date", supplierSelect, productSelectPurchase,
      ["Unpaid", "Partial", "Paid", "Cancelled"],
      [{ name: "po_no", label: "Purchase Order No", type: "text" }, { name: "due_date", label: "Due Date", type: "date" }]).concat([
        { name: "warehouse", label: "Warehouse / Location", type: "text" },
        { name: "batch_number", label: "Batch Number", type: "text" },
        { name: "paid_amount", label: "Paid Amount (₹)", type: "number", min: "0" },
        { name: "paid_mode", label: "Paid Via (Cash / Bank)", type: "select", options: ["Cash", "Bank Transfer", "Cheque", "UPI", "NEFT/RTGS"] },
      ]),
    defaults: { status: "Unpaid", unit: "MT", tax_percent: 5, paid_amount: 0, paid_mode: "Bank Transfer" },
  },
  samples: {
    title: "Sample Dispatch", endpoint: "samples", pdfType: "sample", trackShipment: true, sampleConvert: true,
    searchKeys: ["sample_no", "customer_name", "product_name", "batch_number", "tracking_number", "courier", "sales_person", "grade", "contact_number"],
    statusFilter: ["Prepared", "Dispatched", "In Transit", "Delivered", "Customer Testing", "Approved", "Rejected", "Cancelled"],
    columns: [
      { key: "sample_no", label: "Sample No" },
      { key: "sample_date", label: "Date", format: "date" },
      { key: "customer_name", label: "Customer" },
      { key: "contact_number", label: "Contact" },
      { key: "quantity", label: "Qty", format: "qty" },
      { key: "unit", label: "Unit" },
      { key: "courier", label: "Courier" },
      { key: "tracking_number", label: "AWB" },
      { key: "status", label: "Status", format: "badge" },
    ],
    fields: [
      { name: "sample_no", label: "Sample No", type: "text", required: true },
      { name: "customer_name", label: "Sample Customer Name", type: "text", required: true },
      { name: "contact_number", label: "Contact Number", type: "text" },
      { name: "customer_id", label: "Link Existing Customer (optional)", type: "select", optionsEndpoint: "customers", optionLabel: "company_name", linkName: "customer_name", activeOnly: true, copyFields: { contact_person: "contact_person", destination_address: "shipping_address", sales_person: "sales_person" } },
      { name: "contact_person", label: "Contact Person", type: "text" },
      { name: "product_id", label: "Product", type: "select", optionsEndpoint: "products", optionLabel: "product_name", linkName: "product_name", activeOnly: true, required: true, copyFields: { grade: "grade", unit: "unit" } },
      { name: "grade", label: "Grade", type: "text" },
      { name: "quantity", label: "Quantity", type: "number", step: "0.001", min: "0" },
      { name: "unit", label: "Unit", type: "select", options: UNITS },
      { name: "batch_number", label: "Batch Number", type: "text", required: true },
      { name: "sample_date", label: "Sample Date", type: "date", required: true },
      { name: "dispatch_date", label: "Dispatch Date", type: "date", required: true },
      { name: "courier", label: "Courier / Transporter", type: "select", options: ["DTDC", "Blue Dart", "Delhivery", "FedEx", "India Post", "Gati", "Professional", "VRL Logistics", "Other"], required: true },
      { name: "tracking_number", label: "Tracking / AWB Number", type: "text" },
      { name: "expected_delivery_date", label: "Expected Delivery Date", type: "date" },
      { name: "delivery_date", label: "Delivery Date", type: "date" },
      { name: "destination_address", label: "Destination Address", type: "textarea" },
      { name: "sales_person", label: "Sales Person", type: "text" },
      { name: "purpose", label: "Purpose of Sample", type: "text" },
      { name: "remarks", label: "Remarks", type: "textarea" },
      { name: "deduct_stock", label: "Deduct from Stock?", type: "boolean" },
      { name: "status", label: "Status", type: "select", options: ["Prepared", "Dispatched", "In Transit", "Delivered", "Customer Testing", "Approved", "Rejected", "Cancelled"] },
    ],
    defaults: { status: "Prepared", unit: "KG", deduct_stock: false },
  },
  assets: {
    title: "Assets", endpoint: "assets",
    searchKeys: ["asset_name", "asset_code", "vendor_name", "registration_no"],
    statusFilter: ["In Use", "Under Repair", "Idle", "Sold", "Scrapped"],
    columns: [
      { key: "asset_code", label: "Code" },
      { key: "asset_name", label: "Asset" },
      { key: "category", label: "Category" },
      { key: "purchase_date", label: "Purchased", format: "date" },
      { key: "purchase_cost", label: "Cost", format: "currency" },
      { key: "depreciation_pct", label: "Dep %" },
      { key: "book_value", label: "Book Value", format: "currency" },
      { key: "assigned_to", label: "Assigned To" },
      { key: "status", label: "Status", format: "badge" },
    ],
    fields: [
      { name: "asset_name", label: "Asset Name (e.g. JCB, Bike, Grinder)", type: "text", required: true },
      { name: "category", label: "Category", type: "select", options: ["Machinery", "Vehicle", "Bike", "Transport / Truck", "Plant & Equipment", "Computer / IT", "Furniture", "Other"], required: true },
      { name: "purchase_date", label: "Purchase Date", type: "date", required: true },
      { name: "purchase_cost", label: "Purchase Cost (Rs.)", type: "number", required: true },
      { name: "payment_mode", label: "Paid By", type: "select", options: ["Cash", "Bank Transfer", "Cheque", "UPI", "NEFT/RTGS", "Unpaid"] },
      { name: "vendor_name", label: "Vendor / Seller", type: "text" },
      { name: "bill_no", label: "Bill / Invoice No", type: "text" },
      { name: "depreciation_pct", label: "Depreciation % per year", type: "number" },
      { name: "useful_life_years", label: "Useful Life (years)", type: "number" },
      { name: "registration_no", label: "Registration / Chassis No", type: "text" },
      { name: "insurance_expiry", label: "Insurance Expiry", type: "date" },
      { name: "next_service_date", label: "Next Service / Maintenance Due", type: "date" },
      { name: "location", label: "Location", type: "text" },
      { name: "assigned_to", label: "Assigned To", type: "text" },
      { name: "status", label: "Status", type: "select", options: ["In Use", "Under Repair", "Idle", "Sold", "Scrapped"] },
      { name: "sale_date", label: "Sale / Disposal Date", type: "date", showIf: (f) => ["Sold", "Scrapped"].includes(f.status) },
      { name: "sale_value", label: "Sale Value (Rs.)", type: "number", showIf: (f) => ["Sold", "Scrapped"].includes(f.status) },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    defaults: { status: "In Use", payment_mode: "Bank Transfer" },
    documentsType: "asset", refKey: "asset_code", rowPdfPath: "assets",
  },

};
